import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as DB from '@/lib/supabaseDb';
import { formatTRY } from '@/lib/currencyFormat';
import { toTurkishLower } from '@/lib/customerSearch';
import Badge from '@/components/base/Badge';
import { paymentStatus, deliveryStatus } from '@/lib/statusDefs';

type SortKey = 'sale_no' | 'customer_name' | 'total_amount' | 'remaining_amount' | 'sale_date';
type SortDir = 'asc' | 'desc';

type Period = 'today' | 'this_week' | 'this_month' | 'this_year' | 'all' | 'custom';
type ActiveCardFilter = 'all' | 'collected' | 'remaining' | null;

function getDateRangeForPeriod(period: Period, customStart?: string, customEnd?: string): { start: string; end: string } {
  const now = new Date();
  const istanbulOffset = 3 * 60 * 60 * 1000;
  const trNow = new Date(now.getTime() + istanbulOffset);

  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  switch (period) {
    case 'today': {
      const s = fmt(trNow);
      return { start: s, end: s };
    }
    case 'this_week': {
      const dayOfWeek = trNow.getUTCDay();
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const monday = new Date(trNow);
      monday.setUTCDate(trNow.getUTCDate() - diffToMonday);
      const sunday = new Date(monday);
      sunday.setUTCDate(monday.getUTCDate() + 6);
      return { start: fmt(monday), end: fmt(sunday) };
    }
    case 'this_month': {
      const firstDay = new Date(Date.UTC(trNow.getUTCFullYear(), trNow.getUTCMonth(), 1));
      const lastDay = new Date(Date.UTC(trNow.getUTCFullYear(), trNow.getUTCMonth() + 1, 0));
      return { start: fmt(firstDay), end: fmt(lastDay) };
    }
    case 'this_year': {
      const firstDay = new Date(Date.UTC(trNow.getUTCFullYear(), 0, 1));
      const lastDay = new Date(Date.UTC(trNow.getUTCFullYear(), 11, 31));
      return { start: fmt(firstDay), end: fmt(lastDay) };
    }
    case 'all':
      return { start: '2000-01-01', end: '2099-12-31' };
    case 'custom':
      return { start: customStart || fmt(trNow), end: customEnd || fmt(trNow) };
    default:
      return { start: '2000-01-01', end: '2099-12-31' };
  }
}

const PERIODS: { key: Period; label: string; icon: string }[] = [
  { key: 'today', label: 'Bugün', icon: 'ri-calendar-check-line' },
  { key: 'this_week', label: 'Bu Hafta', icon: 'ri-calendar-2-line' },
  { key: 'this_month', label: 'Bu Ay', icon: 'ri-calendar-line' },
  { key: 'this_year', label: 'Bu Yıl', icon: 'ri-calendar-event-line' },
  { key: 'all', label: 'Tümü', icon: 'ri-global-line' },
];

export default function SalesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const periodFromUrl = (searchParams.get('period') || 'this_month') as Period;
  const customStartFromUrl = searchParams.get('start') || '';
  const customEndFromUrl = searchParams.get('end') || '';

  const [period, setPeriod] = useState<Period>(periodFromUrl);
  const [customStart, setCustomStart] = useState(customStartFromUrl);
  const [customEnd, setCustomEnd] = useState(customEndFromUrl);
  const [showCustomPicker, setShowCustomPicker] = useState(periodFromUrl === 'custom');

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  const [sales, setSales] = useState<any[]>([]);
  const [allSaleItems, setAllSaleItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [salesSummary, setSalesSummary] = useState<DB.SalesSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([DB.getSales(), DB.getAllSaleItems(), DB.getSalesSummary()]).then(([salesRes, itemsRes, summaryRes]) => {
      if (cancelled) return;
      if (salesRes.error) { setError(salesRes.error); setLoading(false); return; }
      setSales((salesRes.data || []) as any[]);
      setAllSaleItems((itemsRes.data || []) as any[]);
      if (summaryRes.data) setSalesSummary(summaryRes.data);
      setLoading(false);
    }).catch(() => { if (!cancelled) { setError('Veri yüklenirken hata oluştu.'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Tümü');
  const [paymentFilter, setPaymentFilter] = useState('Tümü');
  const [deliveryFilter, setDeliveryFilter] = useState('Tümü');
  const [sortKey, setSortKey] = useState<SortKey>('sale_date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [showToast, setShowToast] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const pageSize = 8;

  const paymentStatuses = ['Tümü', 'Ödendi', 'Kısmi Ödendi', 'Borçlu', 'Gecikmiş', 'İptal'];
  const deliveryStatuses = ['Tümü', 'Teslim Edildi', 'Hazırlanıyor', 'Teslimata Hazır', 'Teslim Edilmedi', 'İptal', 'Planlandı', 'Yolda', 'Ertelendi'];
  const categories = ['Tümü', 'Mobilya', 'Profilo', 'Diğer'];

  const dateRange = useMemo(() => getDateRangeForPeriod(period, customStart, customEnd), [period, customStart, customEnd]);

  const updateUrlPeriod = useCallback((p: Period, start?: string, end?: string) => {
    const params = new URLSearchParams();
    params.set('period', p);
    if (p === 'custom' && start && end) {
      params.set('start', start);
      params.set('end', end);
    }
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const handlePeriodChange = useCallback((p: Period) => {
    setPeriod(p);
    if (p === 'custom') {
      setShowCustomPicker(true);
    } else {
      setShowCustomPicker(false);
      updateUrlPeriod(p);
    }
    setCurrentPage(1);
  }, [updateUrlPeriod]);

  const handleCustomApply = useCallback(() => {
    if (customStart && customEnd) {
      updateUrlPeriod('custom', customStart, customEnd);
      setShowCustomPicker(false);
      setCurrentPage(1);
    }
  }, [customStart, customEnd, updateUrlPeriod]);

  const [activeCardFilter, setActiveCardFilter] = useState<ActiveCardFilter>(null);

  const handleCardFilter = useCallback((filter: ActiveCardFilter) => {
    // Aynı karta tekrar tıklanırsa filtreyi kaldır
    if (activeCardFilter === filter) {
      setActiveCardFilter(null);
      setPaymentFilter('Tümü');
      setCurrentPage(1);
      return;
    }
    setActiveCardFilter(filter);
    setCurrentPage(1);
    switch (filter) {
      case 'all':
        setPaymentFilter('Tümü');
        break;
      case 'collected':
        setPaymentFilter('Ödendi');
        break;
      case 'remaining':
        setPaymentFilter('Borçlu');
        break;
      default:
        setPaymentFilter('Tümü');
    }
  }, [activeCardFilter]);

  const filtered = useMemo(() => {
    let result = [...(sales || [])];
    // Tarih filtresi
    result = result.filter(s => {
      const saleDate = s.sale_date || '';
      if (!saleDate) return period === 'all';
      return saleDate >= dateRange.start && saleDate <= dateRange.end;
    });
    if (search) { const q = toTurkishLower(search.trim()); result = result.filter(s => toTurkishLower(s.customer_name || '').includes(q) || toTurkishLower(s.sale_no || '').includes(q)); }
    if (categoryFilter !== 'Tümü') result = result.filter(s => s.product_category === categoryFilter);
    if (paymentFilter !== 'Tümü') result = result.filter(s => s.payment_status === paymentFilter);
    if (deliveryFilter !== 'Tümü') result = result.filter(s => s.delivery_status === deliveryFilter);
    result.sort((a, b) => { const valA = a[sortKey]; const valB = b[sortKey]; if (typeof valA === 'string' && typeof valB === 'string') return sortDir === 'asc' ? valA.localeCompare(valB, 'tr') : valB.localeCompare(valA, 'tr'); return sortDir === 'asc' ? (valA as number) - (valB as number) : (valB as number) - (valA as number); });
    return result;
  }, [sales, search, categoryFilter, paymentFilter, deliveryFilter, sortKey, sortDir, dateRange, period]);

  const summary = useMemo(() => {
    // Tüm zamanlar seçiliyse ve RPC'de maliyet verisi varsa MERKEZİ RPC değerini kullan
    if (period === 'all' && salesSummary && salesSummary.total_cost !== undefined) {
      return {
        salesCount: salesSummary.active_count,
        totalRevenue: salesSummary.total_revenue,
        totalCollected: salesSummary.total_collected,
        totalRemaining: salesSummary.total_remaining,
        totalCost: salesSummary.total_cost,
        grossProfit: salesSummary.gross_profit,
        grossMargin: salesSummary.gross_margin,
        missingCostItems: salesSummary.missing_cost_items,
        hasCostData: salesSummary.missing_cost_items === 0,
      };
    }
    // Tarih filtreli durumda: filtrelenmiş veri üzerinden unit_cost_price kullanarak hesapla
    const nonCancelled = filtered.filter(s => s.payment_status !== 'İptal');
    const saleIds = new Set(nonCancelled.map(s => s.id));
    const relevantItems = (allSaleItems || []).filter((item: any) => saleIds.has(item.sale_id));

    const totalRevenue = nonCancelled.reduce((sum, s) => sum + (s.total_amount || 0), 0);
    const totalCollected = nonCancelled.reduce((sum, s) => sum + (s.paid_amount || 0), 0);
    const totalRemaining = nonCancelled.reduce((sum, s) => sum + (s.remaining_amount || 0), 0);
    const totalCost = relevantItems.reduce((sum: number, item: any) => sum + ((item.unit_cost_price || 0) * (item.quantity || 0)), 0);
    const grossProfit = totalRevenue - totalCost;
    const grossMargin = totalRevenue > 0 ? Math.round((grossProfit / totalRevenue) * 100 * 100) / 100 : 0;
    const missingCostItems = relevantItems.filter((item: any) => !item.unit_cost_price || item.unit_cost_price === 0).length;

    return {
      salesCount: nonCancelled.length,
      totalRevenue,
      totalCollected,
      totalRemaining,
      totalCost,
      grossProfit,
      grossMargin,
      missingCostItems,
      hasCostData: missingCostItems === 0,
    };
  }, [filtered, allSaleItems, period, salesSummary]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = (key: SortKey) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };

  const handleDelete = async () => {
    if (!showDeleteModal || deleting) return;
    const saleId = showDeleteModal;
    setDeleting(true);
    const { error: delErr } = await DB.deleteSaleTransaction(saleId);
    if (delErr) { setShowToast('Silme başarısız: ' + delErr); setDeleting(false); setTimeout(() => setShowToast(null), 3000); return; }
    setShowToast('Satış başarıyla silindi');
    setShowDeleteModal(null);
    setDeleting(false);
    refresh();
    setTimeout(() => setShowToast(null), 3000);
  };

  const sortIcon = (key: SortKey) => sortKey !== key ? <i className="ri-arrow-up-down-line text-foreground-300 text-xs"></i> : <i className={`text-xs text-primary-500 ${sortDir === 'asc' ? 'ri-sort-asc' : 'ri-sort-desc'}`}></i>;

  if (loading) {
    return (
      <div className="space-y-5"><div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"><div><h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Satışlar</h2><p className="text-sm text-foreground-400 mt-0.5">Yükleniyor...</p></div></div><div className="flex flex-col items-center justify-center py-20 gap-4"><div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></div><p className="text-sm text-foreground-400">Satışlar yükleniyor...</p></div></div>
    );
  }

  if (error) {
    return (
      <div className="space-y-5"><div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"><div><h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Satışlar</h2></div></div><div className="flex flex-col items-center justify-center py-20 gap-4"><div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center"><i className="ri-error-warning-line text-2xl text-red-400"></i></div><h3 className="text-lg font-semibold text-foreground-700">Yükleme başarısız</h3><p className="text-sm text-foreground-400">{error}</p><button className="btn-primary mt-2 cursor-pointer" onClick={refresh}>Tekrar Dene</button></div></div>
    );
  }

  return (
    <div className="space-y-5 md:space-y-6">
      {showToast && <div className="fixed top-4 right-4 z-[100] bg-emerald-500 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2"><i className="ri-check-line"></i><span className="text-sm font-medium">{showToast}</span></div>}

      {/* Başlık + Yeni Satış */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Satışlar</h2>
          <p className="text-sm text-foreground-400 mt-0.5">{summary.salesCount} satış · {formatTRY(summary.totalRevenue)} toplam</p>
        </div>
        <button className="btn-primary flex items-center gap-2 self-start cursor-pointer whitespace-nowrap" onClick={() => navigate('/sales/new')}><i className="ri-add-line"></i><span>Yeni Satış</span></button>
      </div>

      {/* Hızlı Tarih Filtreleri */}
      <div className="flex items-center gap-2">
        {/* Desktop: normal tabs */}
        <div className="hidden sm:flex items-center bg-background-50 rounded-full border border-background-200 p-1 gap-0.5">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => handlePeriodChange(p.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${period === p.key ? 'bg-primary-500 text-background-50 shadow-sm' : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'}`}
            ><i className={`${p.icon} text-xs`}></i>{p.label}</button>
          ))}
          <button
            onClick={() => handlePeriodChange('custom')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${period === 'custom' ? 'bg-primary-500 text-background-50 shadow-sm' : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'}`}
          ><i className="ri-calendar-todo-line text-xs"></i>Özel</button>
        </div>

        {/* Mobile: yatay kaydırılabilir sekmeler */}
        <div className="sm:hidden flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => handlePeriodChange(p.key)}
              className={`px-3.5 py-2 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 flex items-center gap-1 ${period === p.key ? 'bg-primary-500 text-background-50' : 'bg-background-50 border border-background-200 text-foreground-500'}`}
            ><i className={`${p.icon} text-[11px]`}></i>{p.label}</button>
          ))}
          <button
            onClick={() => handlePeriodChange('custom')}
            className={`px-3.5 py-2 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 flex items-center gap-1 ${period === 'custom' ? 'bg-primary-500 text-background-50' : 'bg-background-50 border border-background-200 text-foreground-500'}`}
          ><i className="ri-calendar-todo-line text-[11px]"></i>Özel</button>
        </div>
      </div>

      {/* Özel Tarih Seçici */}
      {showCustomPicker && period === 'custom' && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 bg-background-50 border border-background-200 rounded-xl">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-foreground-500 whitespace-nowrap">Başlangıç:</label>
            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="px-3 py-2 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-foreground-500 whitespace-nowrap">Bitiş:</label>
            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="px-3 py-2 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer" />
          </div>
          <button onClick={handleCustomApply} disabled={!customStart || !customEnd} className="btn-primary text-sm cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed">Uygula</button>
        </div>
      )}

      {/* Özet Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <button
          onClick={() => handleCardFilter('all')}
          className={`stat-card p-3 md:p-4 text-left cursor-pointer transition-all duration-200 ${activeCardFilter === 'all' ? 'ring-2 ring-primary-400 bg-primary-50/40 -translate-y-0.5' : 'hover:-translate-y-0.5 hover:border-primary-200/60'}`}
        >
          <span className="text-[10px] md:text-[11px] font-medium text-foreground-400 uppercase tracking-wider">Satış Adedi</span>
          <p className="text-lg md:text-xl font-bold text-foreground-900 mt-1">{summary.salesCount}</p>
        </button>
        <button
          onClick={() => handleCardFilter('all')}
          className={`stat-card p-3 md:p-4 text-left cursor-pointer transition-all duration-200 ${activeCardFilter === 'all' ? 'ring-2 ring-primary-400 bg-primary-50/40 -translate-y-0.5' : 'hover:-translate-y-0.5 hover:border-primary-200/60'}`}
        >
          <span className="text-[10px] md:text-[11px] font-medium text-foreground-400 uppercase tracking-wider">Toplam Satış</span>
          <p className="text-lg md:text-xl font-bold text-foreground-900 mt-1 truncate">{formatTRY(summary.totalRevenue)}</p>
        </button>
        <button
          onClick={() => handleCardFilter('collected')}
          className={`stat-card p-3 md:p-4 text-left cursor-pointer transition-all duration-200 ${activeCardFilter === 'collected' ? 'ring-2 ring-emerald-400 bg-emerald-50/40 -translate-y-0.5' : 'hover:-translate-y-0.5 hover:border-emerald-200/60'}`}
        >
          <span className="text-[10px] md:text-[11px] font-medium text-foreground-400 uppercase tracking-wider">Tahsil Edilen</span>
          <p className="text-lg md:text-xl font-bold text-emerald-600 mt-1 truncate">{formatTRY(summary.totalCollected)}</p>
        </button>
        <button
          onClick={() => handleCardFilter('remaining')}
          className={`stat-card p-3 md:p-4 text-left cursor-pointer transition-all duration-200 ${activeCardFilter === 'remaining' ? 'ring-2 ring-amber-400 bg-amber-50/40 -translate-y-0.5' : 'hover:-translate-y-0.5 hover:border-amber-200/60'}`}
        >
          <span className="text-[10px] md:text-[11px] font-medium text-foreground-400 uppercase tracking-wider">Kalan Alacak</span>
          <p className="text-lg md:text-xl font-bold text-amber-600 mt-1 truncate">{formatTRY(summary.totalRemaining)}</p>
        </button>
        <div className="stat-card p-3 md:p-4">
          <span className="text-[10px] md:text-[11px] font-medium text-foreground-400 uppercase tracking-wider">Toplam Maliyet</span>
          {summary.hasCostData ? (
            <p className="text-lg md:text-xl font-bold text-foreground-700 mt-1 truncate">{formatTRY(summary.totalCost)}</p>
          ) : (
            <p className="text-sm md:text-base font-medium text-foreground-400 mt-1">
              {summary.missingCostItems > 0 ? `${summary.missingCostItems} eksik` : 'Eksik veri'}
            </p>
          )}
        </div>
        <div className="stat-card p-3 md:p-4">
          <span className="text-[10px] md:text-[11px] font-medium text-foreground-400 uppercase tracking-wider">Brüt Kâr</span>
          {summary.hasCostData ? (
            <>
              <p className={`text-lg md:text-xl font-bold mt-1 truncate ${summary.grossProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{formatTRY(summary.grossProfit)}</p>
              <p className="text-[10px] md:text-[11px] text-foreground-400 mt-0.5">Marj: %{summary.grossMargin}</p>
            </>
          ) : summary.missingCostItems > 0 ? (
            <p className="text-sm font-medium text-foreground-400 mt-1">Hesaplanamadı</p>
          ) : (
            <p className="text-sm font-medium text-foreground-400 mt-1">Eksik veri</p>
          )}
        </div>
      </div>

      {/* Arama + Filtreler */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap items-start sm:items-center">
        <div className="relative flex-1 min-w-[240px]"><input type="text" placeholder="Satış no veya müşteri ara..." value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} className="w-full pl-10 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700 placeholder:text-foreground-300" /><i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i></div>
        {activeCardFilter && activeCardFilter !== 'all' && (
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap animate-fade-in ${activeCardFilter === 'collected' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${activeCardFilter === 'collected' ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
            {activeCardFilter === 'collected' ? 'Tahsil Edilenler' : 'Kalan Alacaklar'}
            <button
              onClick={() => { setActiveCardFilter(null); setPaymentFilter('Tümü'); setCurrentPage(1); }}
              className="ml-0.5 w-4 h-4 rounded-full flex items-center justify-center cursor-pointer hover:bg-black/10 transition-colors"
            >
              <i className="ri-close-line text-[10px]"></i>
            </button>
          </div>
        )}
        <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none text-foreground-700 cursor-pointer">{categories.map(c => <option key={c} value={c}>Kategori: {c}</option>)}</select>
        <select value={paymentFilter} onChange={(e) => { setPaymentFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none text-foreground-700 cursor-pointer">{paymentStatuses.map(s => <option key={s} value={s}>Ödeme: {s}</option>)}</select>
        <select value={deliveryFilter} onChange={(e) => { setDeliveryFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none text-foreground-700 cursor-pointer">{deliveryStatuses.map(s => <option key={s} value={s}>Teslimat: {s}</option>)}</select>
      </div>

      {/* Liste */}
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        <div className="hidden lg:block overflow-x-auto"><table className="w-full min-w-[1200px]"><thead><tr className="border-b border-background-200 bg-background-100/50"><th className="table-header cursor-pointer" onClick={() => handleSort('sale_no')}><span className="flex items-center gap-1">Satış No {sortIcon('sale_no')}</span></th><th className="table-header cursor-pointer" onClick={() => handleSort('customer_name')}><span className="flex items-center gap-1">Müşteri {sortIcon('customer_name')}</span></th><th className="table-header">Kategori</th><th className="table-header cursor-pointer" onClick={() => handleSort('total_amount')}><span className="flex items-center gap-1">Tutar {sortIcon('total_amount')}</span></th><th className="table-header">Ödenen</th><th className="table-header cursor-pointer" onClick={() => handleSort('remaining_amount')}><span className="flex items-center gap-1">Kalan {sortIcon('remaining_amount')}</span></th><th className="table-header">Ödeme</th><th className="table-header">Teslimat</th><th className="table-header cursor-pointer" onClick={() => handleSort('sale_date')}><span className="flex items-center gap-1">Tarih {sortIcon('sale_date')}</span></th><th className="table-header text-center">İşlem</th></tr></thead><tbody>{(paginated || []).map(sale => (<tr key={sale.id} className="border-b border-background-100 hover:bg-background-50 transition-colors"><td className="table-cell"><button onClick={() => navigate(`/sales/${sale.id}`)} className="font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap">{sale.sale_no}</button></td><td className="table-cell"><button onClick={() => navigate(`/customers/${sale.customer_id}`)} className="text-foreground-700 hover:text-primary-500 cursor-pointer">{sale.customer_name}</button></td><td className="table-cell text-foreground-400">{sale.product_category}</td><td className="table-cell font-medium">{formatTRY(sale.total_amount || 0)}</td><td className="table-cell text-emerald-600">{formatTRY(sale.paid_amount || 0)}</td><td className="table-cell">{(sale.remaining_amount || 0) > 0 ? <span className="text-red-500 font-medium">{formatTRY(sale.remaining_amount)}</span> : '—'}</td><td className="table-cell py-2"><Badge badgeClass={paymentStatus.badgeClass(sale.payment_status)} icon={paymentStatus.icon(sale.payment_status)}>{sale.payment_status}</Badge></td><td className="table-cell py-2"><Badge badgeClass={deliveryStatus.badgeClass(sale.delivery_status)} icon={deliveryStatus.icon(sale.delivery_status)}>{sale.delivery_status}</Badge></td><td className="table-cell text-foreground-500">{sale.sale_date ? new Date(sale.sale_date).toLocaleDateString('tr-TR') : '—'}</td><td className="table-cell py-2">
            <div className="flex items-center justify-center gap-1.5">
              <button onClick={() => navigate(`/sales/${sale.id}`)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-100 hover:text-primary-600 transition-colors" title="Görüntüle"><i className="ri-eye-line text-sm"></i></button>
              <button onClick={() => navigate(`/sales/${sale.id}/edit`)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-accent-100 hover:text-accent-700 transition-colors" title="Düzenle"><i className="ri-edit-line text-sm"></i></button>
              <button onClick={() => navigate(`/receipt/${sale.id}`)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-accent-100 hover:text-accent-700 transition-colors" title="Makbuz"><i className="ri-printer-line text-sm"></i></button>
              <button onClick={() => setShowDeleteModal(sale.id)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-red-50 hover:text-red-500 transition-colors" title="Sil"><i className="ri-delete-bin-line text-sm"></i></button>
            </div></td></tr>))}{paginated.length === 0 && <tr><td colSpan={10} className="px-4 py-16 text-center"><div className="flex flex-col items-center gap-3"><div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-shopping-cart-2-line text-2xl text-foreground-300"></i></div><p className="text-sm font-medium text-foreground-500">Satış bulunamadı</p><p className="text-xs text-foreground-400">Filtreleri değiştirin veya yeni satış ekleyin</p></div></td></tr>}</tbody></table></div>
        <div className="lg:hidden p-3 space-y-2.5">{(paginated || []).map(sale => (<div key={sale.id} className="bg-background-50 border border-background-200/70 rounded-xl p-3 space-y-2.5"><div className="flex items-center justify-between"><button onClick={() => navigate(`/sales/${sale.id}`)} className="text-xs font-semibold text-primary-600 cursor-pointer">{sale.sale_no}</button><Badge badgeClass={paymentStatus.badgeClass(sale.payment_status)} icon={paymentStatus.icon(sale.payment_status)}>{sale.payment_status}</Badge></div><div className="flex items-center justify-between"><div className="min-w-0 flex-1"><button onClick={() => navigate(`/customers/${sale.customer_id}`)} className="text-sm font-medium text-foreground-800 cursor-pointer hover:text-primary-500 truncate block">{sale.customer_name}</button><p className="text-xs text-foreground-400">{sale.product_category} · {sale.sale_date ? new Date(sale.sale_date).toLocaleDateString('tr-TR') : ''}</p></div><div className="text-right ml-2 flex-shrink-0"><p className="text-sm font-bold text-foreground-800">{formatTRY(sale.total_amount || 0)}</p>{(sale.remaining_amount || 0) > 0 && <p className="text-xs text-red-500">Kalan: {formatTRY(sale.remaining_amount)}</p>}</div></div><div className="flex items-center justify-between"><Badge badgeClass={deliveryStatus.badgeClass(sale.delivery_status)} icon={deliveryStatus.icon(sale.delivery_status)}>{sale.delivery_status}</Badge><div className="flex items-center gap-1"><button onClick={() => navigate(`/sales/${sale.id}`)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-100 hover:text-primary-600"><i className="ri-eye-line text-sm"></i></button><button onClick={() => navigate(`/sales/${sale.id}/edit`)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-accent-100 hover:text-accent-700"><i className="ri-edit-line text-sm"></i></button><button onClick={() => navigate(`/receipt/${sale.id}`)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-accent-100 hover:text-accent-700"><i className="ri-printer-line text-sm"></i></button><button onClick={() => setShowDeleteModal(sale.id)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-red-50 hover:text-red-500"><i className="ri-delete-bin-line text-sm"></i></button></div></div></div>))}{paginated.length === 0 && <div className="flex flex-col items-center gap-3 py-10"><div className="w-12 h-12 rounded-xl bg-background-100 flex items-center justify-center"><i className="ri-shopping-cart-2-line text-xl text-foreground-300"></i></div><p className="text-sm text-foreground-400">Satış bulunamadı</p></div>}</div></div>

      {/* Sayfalama */}
      {totalPages > 1 && <div className="flex items-center justify-between px-5 py-3 border-t border-background-200"><span className="text-xs text-foreground-400">Sayfa {currentPage} / {totalPages}</span><div className="flex items-center gap-1"><button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed"><i className="ri-arrow-left-s-line text-sm"></i></button>{Array.from({ length: totalPages }, (_, i) => i + 1).map(p => <button key={p} onClick={() => setCurrentPage(p)} className={`w-8 h-8 rounded-lg text-sm font-medium cursor-pointer transition-colors ${p === currentPage ? 'bg-primary-500 text-background-50' : 'hover:bg-background-100 text-foreground-600'}`}>{p}</button>)}<button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed"><i className="ri-arrow-right-s-line text-sm"></i></button></div></div>}

      {/* Silme Modalı */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" onClick={() => setShowDeleteModal(null)}><div className="bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}><div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4 mx-auto"><i className="ri-error-warning-line text-xl text-red-500"></i></div><h3 className="text-lg font-semibold text-foreground-900 text-center mb-2">Satışı Sil</h3><p className="text-sm text-foreground-500 text-center mb-6">Bu satışı silmek istediğinize emin misiniz?</p><div className="flex gap-3"><button onClick={() => setShowDeleteModal(null)} disabled={deleting} className="flex-1 btn-outline text-center cursor-pointer">İptal</button><button onClick={handleDelete} disabled={deleting} className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer disabled:opacity-50">{deleting ? 'Siliniyor...' : 'Sil'}</button></div></div></div>
      )}
    </div>
  );
}